import {useEffect, useRef, useState} from "react";
import {HiDotsHorizontal,} from "react-icons/hi";
import {FiEdit3, FiEye, FiTrash2} from "react-icons/fi";
import {useNavigate} from 'react-router-dom';
import {useTemplateService,} from '../../../services';
import {resetTimelineState} from "../../../redux/slices/timeline";
import {resetRoadmap} from "../../../redux/slices/roadmap";
import {useDispatch} from "react-redux";
import { pathWithBreadcrumb } from "../../../utils/pathWithBreadcrumb";
import { Portal } from "@mui/material";

const Options = ({ data= {}, handleClick, activeMenuId, setActiveMenuId }:any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const templateId = data.template_id;
  const isMenuOpen = activeMenuId === templateId;
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { deleteTemplate } = useTemplateService()

  useEffect(() => {
    if (isMenuOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 150;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      const shouldPositionAbove = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

      setDropdownPosition({
        top: shouldPositionAbove
          ? buttonRect.top - dropdownHeight - 8
          : buttonRect.bottom + 8,
        left: buttonRect.right - 250,
      });
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMenuOpen && buttonRef.current) {
        const target = event.target as Node;
        const dropdown = document.querySelector(`[data-dropdown="template-menu-${templateId}"]`);

        if (!buttonRef.current.contains(target) &&
          (!dropdown || !dropdown.contains(target))) {
          setActiveMenuId(null);
        }
      }
    };

    const handleScroll = () => {
      if (isMenuOpen) {
        setActiveMenuId(null);
      }
    };

    const handleResize = () => {
      if (isMenuOpen) {
        setActiveMenuId(null);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isMenuOpen, templateId, setActiveMenuId]);

  const handleMenuToggle = () => {
    if (isMenuOpen) {
      setActiveMenuId(null);
    } else {
      setActiveMenuId(templateId);
    }
  };

  const handleListClick = (data: any, type: string) => {
    setActiveMenuId(null);
    if(type === "edit") {
      handleClick(data)
    }
    if(type === "delete") {
      deleteTemplate(data)
    }
    if(type === "view") {
      dispatch(resetTimelineState());
      dispatch(resetRoadmap());
      navigate(pathWithBreadcrumb({breadcrumb_history: [{name: "Work Items", url: "/workitems"}], current_name: null, url: `/templates/${data.template_id}`}))    }
  }

  return (
    <td className="px-4 py-2 font-medium relative">
      <button
        ref={buttonRef}
        onClick={handleMenuToggle}
        type="button"
        className="inline-flex items-center justify-center w-8 h-8 rounded-full cursor-pointer text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
        title="More actions"
      >
        <HiDotsHorizontal className="w-4 h-4" />
      </button>

      {isMenuOpen && (
        <Portal>
          <div
            data-dropdown={`template-menu-${templateId}`}
            className="fixed min-w-[220px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden z-[99999]"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            <div className="py-2">
              <button
                onClick={() => handleListClick(data, "view")}
                className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm transition-colors duration-150 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <FiEye className="w-4 h-4" />
                <span className="text-left leading-tight">View Template</span>
              </button>
              <button
                onClick={() => handleListClick(data, "edit")}
                className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm transition-colors duration-150 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <FiEdit3 className="w-4 h-4" />
                <span className="text-left leading-tight">
                  Edit Template basic information
                </span>
              </button>
              <button
                onClick={() => handleListClick(data, "delete")}
                className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm transition-colors duration-150 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
              >
                <FiTrash2 className="w-4 h-4" />
                <span className="text-left leading-tight">
                  Delete this template
                </span>
              </button>
            </div>
          </div>
        </Portal>
      )}
    </td>
  );
}

export default Options;